/**
 * Sprint 2.5a /orders-new-redirect, celebration → /chat URL helper.
 *
 * Three customer surfaces previously routed celebrations into the
 * legacy bakery cake-builder at /orders/new (Calendar Approve,
 * Notifications click on approved-without-order, Orders "Needs
 * order" card). That form is heavily pre-pivot, cakeMessage,
 * cake_size, hardcoded VIBE_OPTIONS, and contradicts the multi-
 * vendor positioning at the moment a customer wants to compose a
 * gift for a real recipient.
 *
 * This helper composes a /chat URL that pre-seeds the chat-concierge
 * with everything it needs to start in-context: a natural-language
 * brief ("It's Sarah's birthday on 12 May, help me find a gift")
 * plus the structured params (recipient, recipientId, occasion,
 * date) the chat opener already understands.
 *
 * Reused by:
 *   - Calendar.tsx Approve handler
 *   - Notifications.tsx celebration-click handler
 *   - Orders.tsx "Needs order" card
 *
 * Does NOT touch the edit_order or from_order flows on /orders/new
 *, those still route to the legacy form because chat doesn't yet
 * support editing existing orders.
 */

import { format } from "date-fns";
import { safeFormat } from "@/lib/dates";

interface CelebrationLite {
  id: string;
  type: string;          // "birthday" | "work_anniversary" | etc.
  date: string;          // YYYY-MM-DD
}

interface MemberLite {
  id: string;
  name: string;
  start_date?: string | null;   // YYYY-MM-DD, used to compute work-anniversary year count
}

/**
 * Pretty-print the occasion as it appears in the brief text. The
 * chat-concierge LLM handles the structured `occasion` URL param
 * separately; this is just for the natural-language brief.
 */
function occasionPhrase(type: string, member: MemberLite, celebrationDate: Date): string {
  if (type === "birthday") return "birthday";
  if (type === "work_anniversary") {
    if (member.start_date) {
      try {
        const start = new Date(`${member.start_date}T00:00:00`);
        const years = celebrationDate.getFullYear() - start.getFullYear();
        // Only annotate when the year count is meaningful (>0).
        // The first-year case ("0 years") would read awkwardly.
        if (years > 0) return `${years}-year work anniversary`;
      } catch {
        // Malformed start_date, fall through to the unadorned label.
      }
    }
    return "work anniversary";
  }
  // MS109: explicit cases for the remaining types so the natural-
  // language brief reads coherently and matches the vocabulary used
  // across UI surfaces (MS103-108). Pre-MS109 the snake_case→space
  // fallback would emit "welcome" — consistent with the URL slug
  // but inconsistent with how Calendar / Notifications / Settings
  // refer to that celebration ("first day").
  if (type === "welcome") return "first day";
  if (type === "farewell") return "farewell";
  if (type === "milestone") return "milestone";
  // Truly arbitrary types (e.g. from a future schema extension) —
  // convert snake_case to human-readable so the LLM still gets a
  // sensible brief instead of "team_lunch".
  return type.replace(/_/g, " ");
}

/**
 * Compose a /chat URL from a celebration + member pair.
 *
 * The brief text reads naturally so the LLM can pick it up without
 * additional context: "It's Sarah's 3-year work anniversary on 12
 * May, help me find a gift." The structured params back the brief
 * with machine-readable fields the chat opener uses for slot
 * extraction.
 *
 * Annotates the source so the funnel can measure the celebration→chat
 * carry-through rate separately from cold /chat starts.
 */
export function celebrationToChatUrl(celebration: CelebrationLite, member: MemberLite): string {
  const celDate = (() => {
    try {
      return new Date(`${celebration.date}T00:00:00`);
    } catch {
      return null;
    }
  })();

  const occasion = celDate
    ? occasionPhrase(celebration.type, member, celDate)
    : celebration.type.replace(/_/g, " ");

  // Date phrasing, "on 12 May" reads naturally inside the brief
  // sentence. Year omitted because the celebration is always within
  // the next 90 days (per the calendar's horizon).
  const datePhrase = celDate ? ` on ${format(celDate, "d MMMM")}` : "";

  const brief = `It's ${member.name}'s ${occasion}${datePhrase}, help me find a gift.`;

  const params = new URLSearchParams();
  params.set("brief", brief);
  params.set("recipient", member.name);
  params.set("recipientId", member.id);
  // Send the canonical occasion string (not the human phrase) so the
  // chat opener's structured slot reads it correctly.
  params.set("occasion", celebration.type);
  // The full celebration date, chat-concierge can use this as a
  // delivery hint when proposing options.
  if (celebration.date) params.set("celebration_date", celebration.date);
  // Sprint 2.5a /orders-new-redirect follow-up, celebration_id
  // threads through /chat → OrderConfirmSheet → submit-gift-request
  // so the resulting order links back to the celebration. Without
  // this, /calendar's "Needs order" card would keep flagging the
  // celebration forever even after the customer ordered through chat.
  params.set("celebration_id", celebration.id);
  // Source annotation for funnel attribution.
  params.set("from", "celebration");

  return `/chat?${params.toString()}`;
}

/**
 * Same as celebrationToChatUrl but for callers that only have the
 * celebration_id and need to fetch the celebration + member first
 * (e.g. Notifications.tsx which gets a notification → resolves the
 * celebration row). Caller fetches; this helper composes.
 *
 * Exported separately so the URL composition stays in one place even
 * when the input shape varies across callers.
 */
export function buildCelebrationBriefText(celebration: CelebrationLite, member: MemberLite): string {
  const celDate = (() => {
    try {
      return new Date(`${celebration.date}T00:00:00`);
    } catch {
      return null;
    }
  })();
  const occasion = celDate
    ? occasionPhrase(celebration.type, member, celDate)
    : celebration.type.replace(/_/g, " ");
  // MS238: previously called safeFormat with " on d MMMM" — date-fns
  // treats unquoted alphabetic chars as format tokens, so this format
  // string threw silently and safeFormat fell back to "". Result: the
  // brief text shipped without "on DATE" for years, diverging from
  // celebrationToChatUrl which builds the date phrase via template
  // literal. Mirror that pattern here for consistency.
  const datePhrase = celebration.date
    ? ` on ${safeFormat(celebration.date, "d MMMM", "")}`
    : "";
  return `It's ${member.name}'s ${occasion}${datePhrase}, help me find a gift.`;
}
